// Seed/fixture data for the FF01 prototype. Site names are real RISE
// Commercial District locations for realism (Fishers, Avon, Castleton — all
// Indiana, matching the Expedition brand reference deck). Every tenant,
// contact, balance, work order, message, and lease below is FICTIONAL DEMO
// DATA. "Today" in this demo narrative is Monday, August 31, 2026, matching
// the FF00 flagship scenario (Unit 8203 / Hartley Cold Chain) so FF01 stays
// visually traceable to the approved design direction.
import type {
  Activity,
  COI,
  GeneratedAction,
  Inspection,
  Lease,
  Message,
  MoveInPlan,
  MoveOutPlan,
  Receivable,
  Site,
  SupportRequest,
  Tenant,
  Tour,
  Unit,
  Vendor,
  WorkOrder
} from "./types";

export const TODAY = "2026-08-31";

export const sites: Site[] = [
  { id: "site-fishers", name: "Fishers", city: "Fishers", state: "IN", buildingCount: 3, unitCount: 42 },
  { id: "site-avon", name: "Avon", city: "Avon", state: "IN", buildingCount: 2, unitCount: 27 },
  { id: "site-castleton", name: "Castleton", city: "Castleton", state: "IN", buildingCount: 2, unitCount: 31 }
];

export const vendors: Vendor[] = [
  {
    id: "vendor-meridian",
    name: "Meridian Mechanical",
    trade: "HVAC / Refrigeration",
    contactPhone: "(317) 555-0148",
    performanceNote: "Fastest completion history for cold-storage compressor repairs at this site."
  },
  {
    id: "vendor-hoosier-electric",
    name: "Hoosier Electric Co.",
    trade: "Electrical",
    contactPhone: "(317) 555-0173",
    performanceNote: "Handles all exterior lighting and signage work for Avon."
  },
  {
    id: "vendor-summit-glass",
    name: "Summit Glass & Glazing",
    trade: "Glass / Storefront",
    contactPhone: "(317) 555-0122",
    performanceNote: "Preferred vendor for Castleton storefront turnover work."
  }
];

export const tenants: Tenant[] = [
  {
    id: "tenant-hartley",
    companyName: "Hartley Cold Chain LLC",
    contactName: "Dana Hartley",
    contactEmail: "dana@hartleycoldchain.com",
    contactPhone: "(317) 555-0110",
    tenantSince: "2024-02-01"
  },
  {
    id: "tenant-summit-signworks",
    companyName: "Summit Signworks",
    contactName: "Priya Nair",
    contactEmail: "priya@summitsignworks.com",
    contactPhone: "(317) 555-0132",
    tenantSince: "2026-09-08"
  },
  {
    id: "tenant-vantage-3pl",
    companyName: "Vantage 3PL",
    contactName: "Marcus Webb",
    contactEmail: "marcus@vantage3pl.com",
    contactPhone: "(317) 555-0177",
    tenantSince: "2023-06-15"
  },
  {
    id: "tenant-brightline",
    companyName: "Brightline Packaging",
    contactName: "Jordan Alvarez",
    contactEmail: "jordan@brightlinepkg.com",
    contactPhone: "(317) 555-0199",
    tenantSince: "" // prospect, not a tenant yet
  },
  {
    id: "tenant-avon-metalworks",
    companyName: "Avon Metalworks",
    contactName: "Carla Ito",
    contactEmail: "carla@avonmetalworks.com",
    contactPhone: "(317) 555-0212",
    tenantSince: "2022-11-01"
  },
  {
    id: "tenant-castleton-fit",
    companyName: "Castleton FitLab",
    contactName: "Devon Price",
    contactEmail: "devon@castletonfitlab.com",
    contactPhone: "(317) 555-0264",
    tenantSince: "2025-01-15"
  }
];

export const units: Unit[] = [
  { id: "unit-8203", siteId: "site-fishers", buildingLabel: "Building B", unitNumber: "8203", kind: "warehouse+office", sqft: 4800, tenantId: "tenant-hartley", status: "occupied" },
  { id: "unit-8117", siteId: "site-fishers", buildingLabel: "Building B", unitNumber: "8117", kind: "warehouse", sqft: 3200, tenantId: "tenant-vantage-3pl", status: "occupied" },
  { id: "unit-8014", siteId: "site-fishers", buildingLabel: "Building B", unitNumber: "8014", kind: "flex", sqft: 2400, tenantId: null, status: "vacant" },
  { id: "unit-8022", siteId: "site-fishers", buildingLabel: "Building A", unitNumber: "8022", kind: "office", sqft: 1800, tenantId: "tenant-summit-signworks", status: "leased-pending-move-in" },
  { id: "unit-8110", siteId: "site-fishers", buildingLabel: "Building A", unitNumber: "8110", kind: "warehouse", sqft: 3600, tenantId: "tenant-vantage-3pl", status: "occupied" },
  { id: "unit-8006", siteId: "site-fishers", buildingLabel: "Building A", unitNumber: "8006", kind: "warehouse", sqft: 5200, tenantId: null, status: "vacant" },
  { id: "unit-avon-140", siteId: "site-avon", buildingLabel: "Building 1", unitNumber: "140", kind: "warehouse", sqft: 6000, tenantId: "tenant-avon-metalworks", status: "occupied" },
  { id: "unit-avon-212", siteId: "site-avon", buildingLabel: "Building 2", unitNumber: "212", kind: "office", sqft: 1400, tenantId: null, status: "turnover" },
  { id: "unit-castleton-305", siteId: "site-castleton", buildingLabel: "Building C", unitNumber: "305", kind: "flex", sqft: 2100, tenantId: "tenant-castleton-fit", status: "occupied" },
  { id: "unit-castleton-410", siteId: "site-castleton", buildingLabel: "Building D", unitNumber: "410", kind: "office", sqft: 1600, tenantId: null, status: "vacant" }
];

export const leases: Lease[] = [
  { id: "lease-8203", unitId: "unit-8203", tenantId: "tenant-hartley", monthlyRent: 2450, startDate: "2024-02-01", endDate: "2027-01-31", renewalWindowOpensDate: "2026-10-11" },
  { id: "lease-8117", unitId: "unit-8117", tenantId: "tenant-vantage-3pl", monthlyRent: 1900, startDate: "2023-06-15", endDate: "2026-12-14", renewalWindowOpensDate: "2026-09-14" },
  { id: "lease-8022", unitId: "unit-8022", tenantId: "tenant-summit-signworks", monthlyRent: 1650, startDate: "2026-09-08", endDate: "2029-09-07", renewalWindowOpensDate: "2029-06-08" },
  { id: "lease-8110", unitId: "unit-8110", tenantId: "tenant-vantage-3pl", monthlyRent: 2100, startDate: "2023-09-01", endDate: "2026-09-30", renewalWindowOpensDate: "2026-06-30" },
  { id: "lease-avon-140", unitId: "unit-avon-140", tenantId: "tenant-avon-metalworks", monthlyRent: 3200, startDate: "2022-11-01", endDate: "2027-10-31", renewalWindowOpensDate: "2027-07-31" },
  { id: "lease-castleton-305", unitId: "unit-castleton-305", tenantId: "tenant-castleton-fit", monthlyRent: 1750, startDate: "2025-01-15", endDate: "2028-01-14", renewalWindowOpensDate: "2027-10-15" }
];

export const receivables: Receivable[] = [
  { id: "ar-8203", leaseId: "lease-8203", balanceDue: 2450, daysLate: 12, lastReminderSentAt: "2026-08-28T09:15:00" },
  { id: "ar-8110", leaseId: "lease-8110", balanceDue: 1670, daysLate: 4, lastReminderSentAt: null }
];

export const workOrders: WorkOrder[] = [
  {
    id: "wo-1187",
    unitId: "unit-8203",
    title: "Compressor fault — cold storage",
    description: "Cold-storage compressor tripped repeatedly during a heat event; tenant reported product loss risk.",
    status: "ready-to-verify",
    vendorId: "vendor-meridian",
    reportedAt: "2026-07-30T08:10:00",
    repairedAt: "2026-08-22T14:00:00",
    verifiedAt: null,
    verificationResult: null,
    history: [
      { status: "reported", at: "2026-07-30T08:10:00", note: "Tenant called in — compressor cycling off." },
      { status: "assigned", at: "2026-07-30T11:00:00", note: "Dispatched to Meridian Mechanical." },
      { status: "in-progress", at: "2026-08-05T09:00:00", note: "Part on order — condenser fan motor." },
      { status: "ready-to-verify", at: "2026-08-22T14:00:00", note: "Meridian marked repair complete; awaiting DM verification." }
    ]
  },
  {
    id: "wo-1163",
    unitId: "unit-8117",
    title: "Dock door repair",
    description: "Dock door #2 spring failed, door will not stay raised.",
    status: "ready-to-verify",
    vendorId: "vendor-meridian",
    reportedAt: "2026-08-25T10:00:00",
    repairedAt: "2026-08-28T16:30:00",
    verifiedAt: null,
    verificationResult: null,
    history: [
      { status: "reported", at: "2026-08-25T10:00:00" },
      { status: "assigned", at: "2026-08-25T13:00:00" },
      { status: "in-progress", at: "2026-08-27T09:00:00" },
      { status: "ready-to-verify", at: "2026-08-28T16:30:00", note: "Meridian marked done Friday." }
    ]
  },
  {
    id: "wo-1201",
    unitId: "unit-avon-212",
    title: "Storefront glass replacement",
    description: "Cracked storefront panel from prior tenant move-out — part of turnover scope.",
    status: "in-progress",
    vendorId: "vendor-summit-glass",
    reportedAt: "2026-08-20T09:00:00",
    repairedAt: null,
    verifiedAt: null,
    verificationResult: null,
    history: [
      { status: "reported", at: "2026-08-20T09:00:00" },
      { status: "assigned", at: "2026-08-21T09:00:00" },
      { status: "in-progress", at: "2026-08-26T09:00:00", note: "Glass ordered, install scheduled this week." }
    ]
  }
];

export const tours: Tour[] = [
  {
    id: "tour-brightline",
    siteId: "site-fishers",
    unitId: "unit-8014",
    prospectCompany: "Brightline Packaging",
    prospectContact: "Jordan Alvarez",
    source: "hubspot",
    scheduledAt: "2026-08-31T10:00:00",
    status: "scheduled",
    leadTemperature: "hot",
    useCase: "Light packaging + small office presence",
    budgetRange: "$2,000–$2,500 / mo",
    notes: ["Wants fast move-in.", "Currently in a month-to-month sublease that ends Sep 30."],
    outcome: null
  },
  {
    id: "tour-avon-prospect",
    siteId: "site-avon",
    unitId: "unit-avon-212",
    prospectCompany: "Heartland Fixtures Co.",
    prospectContact: "Ray Solano",
    source: "google-calendar",
    scheduledAt: "2026-09-02T14:00:00",
    status: "scheduled",
    leadTemperature: "warm",
    useCase: "Small showroom + office",
    budgetRange: "$1,400–$1,700 / mo",
    notes: [],
    outcome: null
  }
];

export const supportRequests: SupportRequest[] = [];

export const cois: COI[] = [
  { id: "coi-8110", unitId: "unit-8110", tenantId: "tenant-vantage-3pl", status: "flagged", expiresOn: "2026-09-30" },
  { id: "coi-8203", unitId: "unit-8203", tenantId: "tenant-hartley", status: "verified", expiresOn: "2026-12-31" }
];

export const moveInPlans: MoveInPlan[] = [
  {
    id: "movein-8022",
    unitId: "unit-8022",
    tenantId: "tenant-summit-signworks",
    targetMoveInDate: "2026-09-08",
    status: "in-progress",
    items: [
      { key: "unit-inspection", label: "Unit inspection", done: true },
      { key: "interior-photos", label: "Interior condition photos", done: true },
      { key: "exterior-photos", label: "Exterior condition photos", done: false },
      { key: "access-setup", label: "Access fobs + door codes issued", done: false },
      { key: "internet-service", label: "Internet / service activation confirmed", done: true },
      { key: "signage", label: "Suite signage installed", done: true },
      { key: "welcome-confirmation", label: "Tenant welcome + move-in confirmation sent", done: false }
    ]
  }
];

export const moveOutPlans: MoveOutPlan[] = [
  {
    id: "moveout-avon-212",
    unitId: "unit-avon-212",
    tenantId: "tenant-avon-metalworks", // prior tenant of this now-vacant unit, for demo lineage
    noticeDate: "2026-07-15",
    moveOutDate: "2026-08-15",
    stage: "turnover-actions",
    depositOnFile: 2100,
    amountRetained: null,
    retentionReason: null
  }
];

export const activities: Activity[] = [
  { id: "act-1", unitId: "unit-8203", siteId: "site-fishers", at: "2026-08-28T09:15:00", kind: "automation", summary: "Payment reminder sent — email, no reply yet." },
  { id: "act-2", unitId: "unit-8203", siteId: "site-fishers", at: "2026-08-22T14:05:00", kind: "automation", summary: "Reinspection auto-scheduled after vendor completion." },
  { id: "act-3", unitId: "unit-8203", siteId: "site-fishers", at: "2026-08-19T11:30:00", kind: "manual", summary: "Dana Hartley called — asked about repair timeline." },
  { id: "act-4", unitId: "unit-8203", siteId: "site-fishers", at: "2026-07-30T08:10:00", kind: "system", summary: "Compressor fault reported (WO-1187)." }
];

export const messages: Message[] = [];

export const generatedActionsSeed: GeneratedAction[] = [];

export const inspectionsSeed: Inspection[] = [
  {
    id: "insp-fishers-grounds",
    siteId: "site-fishers",
    unitId: null,
    title: "Weekly grounds inspection — Building C exterior",
    status: "not-started",
    currentStepIndex: 0,
    startedAt: null,
    completedAt: null,
    items: [
      { id: "insp-item-1", category: "Perimeter", title: "Fencing & gate condition", hint: "Check for damage or forced-entry signs along the north fence line.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-2", category: "Signage", title: "Monument sign lighting", hint: "Confirm all faces are lit; last visit flagged a bulb out on the east face.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-3", category: "Landscaping", title: "Landscaping & drainage", hint: "Look for standing water near the retention pond inlet.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-4", category: "Signage", title: "Wayfinding signage", hint: "Confirm suite directory is current after the 8022 signage install.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-5", category: "Lighting", title: "Parking lot lighting — Row C", hint: "Row C had two poles flagged for flicker last month.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-6", category: "Dock & Loading", title: "Dock apron condition — Bays 4–6", hint: "Last visit flagged hairline cracking at Bay 5 — compare and record.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-7", category: "Dock & Loading", title: "Dock door seals — Bays 1–3", hint: "Check weather seals for wear before fall.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-8", category: "Life Safety", title: "Exterior fire lane markings", hint: "Confirm markings are visible and unobstructed.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-9", category: "Life Safety", title: "Exterior fire hydrant access", hint: "Confirm 3-foot clearance is maintained.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-10", category: "General", title: "Trash / compactor area", hint: "Check for overflow and gate closure.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-11", category: "General", title: "Common-area entry doors", hint: "Confirm self-closing hardware operates correctly.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null },
      { id: "insp-item-12", category: "General", title: "Overall exterior walk summary", hint: "Note any condition not covered above.", result: null, photoIds: [], note: null, voiceTranscript: null, generatedActionId: null }
    ]
  }
];
